import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, Grid, TextField, Button, MenuItem, Box, IconButton, Divider, Alert
} from '@mui/material';
import { Add, Remove, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const billNameOptions = ['P', 'K', 'R', 'O', 'W', 'Y'];
const unitOptions = ['Pcs', 'Pair', 'Set'];
const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];

const itemNameOptions = [
  'Gold Ring',
  'Gold Chain',
  'Gold Necklace',
  'Gold Bracelet',
  'Gold Earring',
  'Gold Pendant',
  'Silver Ring',
  'Silver Chain',
  'Silver Bracelet',
  'Silver Earring',
  'Silver Pendant',
  'Platinum Ring',
  'Platinum Chain',
  'Diamond Ring',
  'Diamond Necklace'
];

const defaultItem = {
  itemName: '',
  quantity: 1,
  unit: 'Pcs',
  size: '',
  description: '',
  gsb: 0,
  itemWeight: 0,
  wastage: 0,
  total: 0,
  karat: '22K',
  rate: 0,
  netAmount: 0,
  makingCharge: 0,
  stoneAmount: 0,
  totalAmount: 0
};

const defaultCustomer = {
  name: '',
  karigarId: '',
  address: '',
  phoneNumber: ''
};

const OrderForm = () => {
  const [form, setForm] = useState({
    billName: '',
    billNumber: '',
    orderDate: '',
    deliveryDate: '',
    requiredDate: '',
    status: 'Pending',
    agentName: '',
    customer: { ...defaultCustomer },
    items: [{ ...defaultItem }]
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [itemOptions, setItemOptions] = useState([]);
  const [orderId, setOrderId] = useState(null);
  const [shouldFetchOrder, setShouldFetchOrder] = useState(false);
  const lastAttemptedBillName = useRef('');
  const lastAttemptedBillNumber = useRef('');
  const [orderExists, setOrderExists] = useState(false);
  
  // Debug effect to track orderExists changes
  useEffect(() => {
    console.log('orderExists changed to:', orderExists);
  }, [orderExists]);

  const fetchItems = async () => {
    try {
      const res = await api.get('/api/items');
      setItemOptions(res.data.map(item => item.name));
    } catch (err) {
      // fallback: do nothing
    }
  };

  useEffect(() => {
    fetchItems();
    // Only pre-fill from localStorage if form is empty
    setForm(prev => {
      if (!prev.billName && !prev.billNumber) {
        const storedBillName = localStorage.getItem('sharedBillName');
        const storedBillNumber = localStorage.getItem('sharedBillNumber');
        return {
          ...prev,
          billName: storedBillName || '',
          billNumber: storedBillNumber || ''
        };
      }
      return prev;
    });
  }, []);

  // Only fetch when shouldFetchOrder is true and both fields are filled
  useEffect(() => {
    if (shouldFetchOrder && form.billName && form.billNumber) {
      console.log('Checking for existing order:', form.billName, form.billNumber);
      // Reset orderExists to false before making the API call
      setOrderExists(false);
      
      api.get(`/api/shop/orders?billName=${encodeURIComponent(form.billName)}&billNumber=${encodeURIComponent(form.billNumber)}`)
        .then(res => {
          console.log('API response:', res.data);
          if (Array.isArray(res.data) && res.data.length > 0) {
            console.log('Order exists, setting orderExists to true');
            setOrderExists(true);
            // Optionally, show a warning here
          } else {
            console.log('Order does not exist, setting orderExists to false');
            setOrderExists(false);
            setForm({
              billName: lastAttemptedBillName.current,
              billNumber: lastAttemptedBillNumber.current,
              orderDate: '',
              deliveryDate: '',
              requiredDate: '',
              status: 'Pending',
              agentName: '',
              customer: { name: '', karigarId: '', address: '', phoneNumber: '' },
              items: [{
                itemName: '',
                quantity: 1,
                unit: 'Pcs',
                size: '',
                description: '',
                gsb: 0,
                itemWeight: 0,
                wastage: 0,
                total: 0,
                karat: '22K',
                rate: 0,
                netAmount: 0,
                makingCharge: 0,
                stoneAmount: 0,
                totalAmount: 0
              }],
              totalAmount: 0
            });
          }
        })
        .catch((error) => { 
          console.error('Error checking for existing order:', error);
          console.log('Setting orderExists to false due to error');
          setOrderExists(false); 
        });
      setShouldFetchOrder(false);
    }
  }, [shouldFetchOrder, form.billName, form.billNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;
  setForm(prev => ({ ...prev, [name]: value }));
    // Synchronize billName and billNumber to localStorage
    if (name === 'billName') localStorage.setItem('sharedBillName', value);
    if (name === 'billNumber') localStorage.setItem('sharedBillNumber', value);
    // Clear orderId and reset orderExists if billName or billNumber changes
    if (name === 'billName' || name === 'billNumber') {
      setOrderId(null);
      setOrderExists(false);
    }
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, customer: { ...prev.customer, [name]: value } }));
  };

  const handleItemChange = (idx, e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === idx ? { ...item, [name]: value } : item)
    }));
  };

  const validate = () => {
    if (!form.billName || !form.billNumber || !form.orderDate || !form.deliveryDate || !form.requiredDate || !form.agentName) return false;
    if (!form.customer.name || !form.customer.karigarId || !form.customer.address || !form.customer.phoneNumber) return false;
    if (!form.items.length) return false;
    for (let i = 0; i < computedItems.length; i++) {
      const item = computedItems[i];
      if (!item.itemName || !item.quantity || !item.unit || !item.itemWeight || !item.karat) return false;
      if (!item.total || !item.netAmount || !item.totalAmount) return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validate()) {
      setError('Please fill all required fields.');
      return;
    }
    if (orderExists) {
      setError('This bill already exists. Please use the Update Order form to edit.');
      return;
    }
    console.log('Submitting order with orderExists:', orderExists);
    setLoading(true);
    try {
      // Calculate totalAmount for each item and for the order
      const computedItems = form.items.map(item => {
        const weight = parseFloat(item.itemWeight) || 0;
        const wastage = parseFloat(item.wastage) || 0;
        const rate = parseFloat(item.rate) || 0;
        const makingCharge = parseFloat(item.makingCharge) || 0;
        const stoneAmount = parseFloat(item.stoneAmount) || 0;
        const total = weight + wastage;
        const netAmount = weight ? ((weight / 11.664) * rate) : 0;
        const totalAmount = netAmount + makingCharge + stoneAmount;
        return {
          ...item,
          total: total ? total.toFixed(3) : '',
          netAmount: netAmount ? netAmount.toFixed(2) : '',
          totalAmount: totalAmount ? totalAmount.toFixed(2) : ''
        };
      });
      const orderTotalAmount = computedItems.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
      const payload = {
        ...form,
        orderDate: new Date(form.orderDate),
        deliveryDate: new Date(form.deliveryDate),
        requiredDate: new Date(form.requiredDate),
        items: computedItems.map(item => ({
          ...item,
          quantity: Number(item.quantity),
          gsb: Number(item.gsb),
          itemWeight: Number(item.itemWeight),
          wastage: Number(item.wastage),
          total: Number(item.total),
          rate: Number(item.rate),
          netAmount: Number(item.netAmount),
          makingCharge: Number(item.makingCharge),
          stoneAmount: Number(item.stoneAmount),
          totalAmount: Number(item.totalAmount)
        })),
        totalAmount: orderTotalAmount
      };
      await api.post('/api/shop/orders', payload);
      setSuccess('Order created successfully!');
      setForm({
        billName: '',
        billNumber: '',
        orderDate: '',
        deliveryDate: '',
        requiredDate: '',
        status: 'Pending',
        agentName: '',
        customer: { name: '', karigarId: '', address: '', phoneNumber: '' },
        items: [{
          itemName: '',
          quantity: 1,
          unit: 'Pcs',
          size: '',
          description: '',
          gsb: 0,
          itemWeight: 0,
          wastage: 0,
          total: 0,
          karat: '22K',
          rate: 0,
          netAmount: 0,
          makingCharge: 0,
          stoneAmount: 0,
          totalAmount: 0
        }],
        totalAmount: 0
      });
      localStorage.removeItem('sharedBillName');
      localStorage.removeItem('sharedBillNumber');
      setTimeout(() => navigate('/shop/orders'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create order');
    }
    setLoading(false);
  };

  const computedItems = form.items.map(item => {
    const weight = parseFloat(item.itemWeight) || 0;
    const wastage = parseFloat(item.wastage) || 0;
    const rate = parseFloat(item.rate) || 0;
    const makingCharge = parseFloat(item.makingCharge) || 0;
    const stoneAmount = parseFloat(item.stoneAmount) || 0;
    const total = weight + wastage;
    const netAmount = weight ? ((weight / 11.664) * rate) : 0;
    const totalAmount = netAmount + makingCharge + stoneAmount;
    return {
      ...item,
      total: total ? total.toFixed(3) : '',
      netAmount: netAmount ? netAmount.toFixed(2) : '',
      totalAmount: totalAmount ? totalAmount.toFixed(2) : ''
    };
  });

  return (
    <Container maxWidth={false} sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" color="primary">Back</Button>
          <Button variant="outlined" color="secondary" onClick={() => navigate('/update-order')}>Update Existing Order</Button>
        </Box>
        <Typography variant="h4" gutterBottom color="primary">
          Order Entry
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {orderExists && <Alert severity="warning" sx={{ mb: 2 }}>This bill already exists. Please use the Update Order form to edit.</Alert>}
        {/* Nepali Date Display */}
        <Box sx={{ mb: 2, display: 'flex', gap: 4 }}>
          {form.orderDate && (
            <Typography variant="body2" color="secondary">
            </Typography>
          )}
          {form.deliveryDate && (
            <Typography variant="body2" color="secondary">
            </Typography>
          )}
          {form.requiredDate && (
            <Typography variant="body2" color="secondary">
            </Typography>
          )}
        </Box>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={2}>
              <TextField select label="Bill Name" name="billName" value={form.billName} onChange={handleChange} onBlur={e => { lastAttemptedBillName.current = e.target.value; setShouldFetchOrder(true); }} required fullWidth>
                {billNameOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Bill Number" name="billNumber" value={form.billNumber} onChange={handleChange} onBlur={e => { lastAttemptedBillNumber.current = e.target.value; setShouldFetchOrder(true); }} required fullWidth />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Order Date"
                name="orderDate"
                type="date"
                value={form.orderDate}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Delivery Date"
                name="deliveryDate"
                type="date"
                value={form.deliveryDate}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Required Date"
                name="requiredDate"
                type="date"
                value={form.requiredDate}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField select label="Status" name="status" value={form.status} onChange={handleChange} required fullWidth>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Agent Name (Salesman)" name="agentName" value={form.agentName} onChange={handleChange} required fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Customer Name" name="name" value={form.customer.name} onChange={handleCustomerChange} required fullWidth />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Karigar ID" name="karigarId" value={form.customer.karigarId} onChange={handleCustomerChange} required fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Address" name="address" value={form.customer.address} onChange={handleCustomerChange} required fullWidth />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Phone Number" name="phoneNumber" value={form.customer.phoneNumber} onChange={handleCustomerChange} required fullWidth />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Order Items</Typography>
            </Grid>
            {computedItems.map((item, idx) => (
              <React.Fragment key={idx}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label="Item Name"
                      name="itemName"
                      value={item.itemName}
                      onChange={e => handleItemChange(idx, e)}
                      required
                      fullWidth
                    >
                      {itemOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField type="number" label="Qty" name="quantity" value={item.quantity} onChange={e => handleItemChange(idx, e)} required fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField select label="Unit" name="unit" value={item.unit} onChange={e => handleItemChange(idx, e)} required fullWidth>
                      {unitOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField label="Size" name="size" value={item.size} onChange={e => handleItemChange(idx, e)} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField label="Description" name="description" value={item.description} onChange={e => handleItemChange(idx, e)} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField label="GSB" name="gsb" value={item.gsb} onChange={e => handleItemChange(idx, e)} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField type="number" label="Weight" name="itemWeight" value={item.itemWeight} onChange={e => handleItemChange(idx, e)} required fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField type="number" label="Wastage" name="wastage" value={item.wastage} onChange={e => handleItemChange(idx, e)} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField label="Total" name="total" value={item.total} fullWidth InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField select label="Karat" name="karat" value={item.karat} onChange={e => handleItemChange(idx, e)} required fullWidth>
                      {karatOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField type="number" label="Rate" name="rate" value={item.rate} onChange={e => handleItemChange(idx, e)} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField label="Net Amt" name="netAmount" value={item.netAmount} fullWidth InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField type="number" label="Making Chg" name="makingCharge" value={item.makingCharge} onChange={e => handleItemChange(idx, e)} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField type="number" label="Stone Amt" name="stoneAmount" value={item.stoneAmount} onChange={e => handleItemChange(idx, e)} fullWidth />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <TextField label="Total Amt" name="totalAmount" value={item.totalAmount} fullWidth InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton onClick={() => {
                      setForm(prev => ({
                        ...prev,
                        items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== idx) : prev.items
                      }));
                    }} disabled={computedItems.length === 1}><Remove /></IconButton>
                    <IconButton onClick={() => {
                      setForm(prev => ({
                        ...prev,
                        items: [...prev.items, { ...defaultItem }]
                      }));
                    }}><Add /></IconButton>
                  </Grid>
                </Grid>
              </React.Fragment>
            ))}
            <Divider sx={{ my: 3 }} />
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Order'}
            </Button>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default OrderForm;